<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { object, array, string } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DLoading,
  DTextAreaField,
  Trash03Icon,
  PlusIcon
} from 'front-library'
import {
  ICreateSubscriptionManagement,
  createSubscriptionManagement,
  updateSubscriptionManagement,
  getTrigger
} from '@/app/core/services/subscriptions-management'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* store, router, notifications */
const store = useUserStore()
const router = useRouter()
const notifications = useNotificationsStore()
const route = useRoute()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const itemsEvents = ref<any[]>([
  { id: 'PAYROLL-CLOSED', name: t('subscriptions.fields.closePayroll') }
])

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  name: string()
    .min(1, t('form.valid.minLength', [1]))
    .max(100, t('form.valid.maxLength', [100]))
    .required(t('form.valid.required')),
  endpoint: string()
    .matches(
      /^((http|https):\/\/)?(www.)?(?!.*(http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+(\/)?.([\w\?[a-zA-Z-_%\/@?]+)*([^\/\w\?[a-zA-Z0-9_-]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/,
      t('subscriptions.valid.url')
    )
    .required(t('form.valid.required')),
  events: array()
    .min(1, t('form.valid.minLength', [1]))
    .required(t('form.valid.required')),
  description: string().max(1000, t('form.valid.maxLength', [1000])),
  key: string().max(1000, t('form.valid.maxLength', [1000])),
  value: string().max(1000, t('form.valid.maxLength', [1000]))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any /*paramentBonus*/>({
  initialValues: { headers: [] },
  validationSchema
})

/* Definicion campos del formulario */
const { value: name } = useField<string>('name')
const { value: endpoint } = useField<string>(ref('endpoint'))
const { value: description } = useField<string>(ref('description'))
const { value: events } = useField<string[]>(ref('events'))
const { value: headers } = useField<{ key: string; value: string }[]>(
  ref('headers')
)
const { value: key } = useField<string>(ref('key'))
const { value: value } = useField<string>(ref('value'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: ICreateSubscriptionManagement) => {
  void createSubscription(values)
})

/* Crear solicitud del reporte */
const createSubscription = async (values: any) => {
  loading.value = true
  messageLoading.value = t('subscriptions.loading.subscription')
  try {
    if (!route.params.id) {
      const response: any = await createSubscriptionManagement(values)
    } else {
      const response: any = await updateSubscriptionManagement(
        values,
        route.params.id as string
      )
    }
    loading.value = false
    notifications.addNotification({
      title: !route.params.id
        ? t('notification.created')
        : t('notification.edited'),
      text: t('subscriptions.notification.action', [
        !route.params.id
          ? t('notification.wasCreated')
          : t('notification.wasEdited')
      ]),
      type: 'success',
      time: 10000
    })
    void router.push({ name: 'ListSubscriptionsManagement' })
  } catch (error: any) {
    notificationError(error, 'subscriptions', t, notifications, loading)
  }
}

/* Agregar header */
const addHeader = async () => {
  if (!key.value || !value.value) return
  headers.value.push({ key: key.value, value: value.value })
  key.value = ''
  value.value = ''
}

/* Eliminar header */
const deleteHeader = async (item: any) => {
  let indexOf = headers.value.indexOf(item)
  if (indexOf > -1) {
    headers.value.splice(indexOf, 1)
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListSubscriptionsManagement' })
}

onMounted(async () => {
  if (!route.params.id) return
  loading.value = true
  try {
    const data = await getTrigger(route.params.id as string)
    if (data === undefined) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('subscriptions.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    }
    name.value = data.name
    endpoint.value = data.endpoint
    events.value = data.events
    description.value = data.description
    headers.value = data.headers
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('subscriptions.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'subscriptions', t, notifications, loading)
    }
  }
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="
            route.params.id == undefined
              ? t('subscriptions.create')
              : t('subscriptions.edit')
          " />
      </div>
    </div>
    <form class="scroll overflow-auto h-subscription">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('subscriptions.fields.name')"
                    v-model="name"
                    :required="true"
                    :error="errors.name" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="text"
                    :label="t('subscriptions.fields.endpoint')"
                    v-model="endpoint"
                    :required="true"
                    :error="errors.endpoint" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('subscriptions.fields.events')"
                    v-model="events"
                    :headerTitle="t('subscriptions.fields.events')"
                    :required="true"
                    :options="itemsEvents"
                    :error="errors.events" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-6">
                  <DTextAreaField
                    :label="t('subscriptions.fields.description')"
                    v-model="description"
                    :error="errors.description" />
                </div>
                <hr class="col-span-6" />
                <h1
                  class="col-span-6 sm:col-span-3 lg:col-span- flex items-center gap-3">
                  Headers
                </h1>
                <div
                  class="flex flex-row gap-2 col-span-4 justify-around items-end">
                  <div class="w-full">
                    <DTextField
                      type="text"
                      label="Key"
                      v-model="key"
                      :error="errors.key" />
                  </div>
                  <div class="w-full">
                    <DTextField
                      type="text"
                      label="Value"
                      v-model="value"
                      :error="errors.value" />
                  </div>
                  <div
                    class="bg-blue-100 rounded-md cursor-pointer py-2"
                    @click="addHeader()">
                    <PlusIcon class="h-6 w-6 mx-2 text-blue-600" />
                  </div>
                </div>
                <hr class="col-span-6" />
                <div
                  class="flex flex-row gap-2 col-span-4 justify-between items-end"
                  v-for="(header, index) in headers"
                  :key="index">
                  <div class="w-full">
                    <DTextField type="text" label="Key" v-model="header.key" />
                  </div>
                  <div class="w-full">
                    <DTextField
                      type="text"
                      label="Value"
                      v-model="header.value" />
                  </div>
                  <div
                    class="bg-gray-50 rounded-md cursor-pointer py-2"
                    @click="deleteHeader(header)">
                    <Trash03Icon class="h-6 w-6 mx-2 text-gray-700" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.save')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
<style scoped>
.h-subscription {
  height: calc(100% - 8rem);
}
@media (max-width: 1023px) {
  .h-subscription {
    height: calc(100% - 12rem);
  }
}
</style>
